import { Button, Card, Divider, Flex, Form, Input, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useLoader } from "../../Context/LoaderContext";
import AuthService from "../../Service/auth.service";
import SuccessResult from "../../Components/SuccessResult/SuccessResult";
import FailureResult from "../../Components/FailureResult/FailureResult";

const failureReasons = [
  "The password reset link has expired or is invalid.",
  "Server or email service problems are preventing the reset process from working correctly.",
];

const ResetPassword = () => {
  const navigate = useNavigate();
  const { setLoading, setLoadingMessage, isLoading } = useLoader();
  const [showFailure, setShowFailure] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const authService = AuthService.getInstance();

  const handlePasswordSubmit = async (values: any) => {
    if (token) {
      setLoading(true);
      setLoadingMessage("Resetting password please wait...");
      const password = values.password;
      const response = await authService.resetPassword(token, password);
      if (response?.status === 201) {
        setLoading(false);
        setShowSuccess(true);
        setLoadingMessage(null);
      } else {
        setLoading(false);
        setShowFailure(true);
        setLoadingMessage(null);
      }
    } else {
      console.log("No token while resting password");
    }
  };

  const handleGoToSignIn = () => {
    navigate("/sign-in");
  };

  const handleTryAgain = () => {
    setShowFailure(false);
    setShowSuccess(false);
    navigate("/forgot-password");
  };
  return (
    <Flex className="signin" align="center" justify="center" style={{ height: "100%" }}>
      {!showFailure && !showSuccess && (
        <Card
          className="card"
          bordered
          style={{ maxWidth: 360, width: "100%" }}
          title={
              <Typography.Title level={4} style={{ margin: 0 }}>
                Reset Password
              </Typography.Title>
          }
        >
          <Form
            name="password_reset_form"
            className="login-form"
            initialValues={{ remember: true }}
            onFinish={handlePasswordSubmit}
            autoComplete="off"
            scrollToFirstError
          >
            <Form.Item
              name="password"
              rules={[
                {
                  required: true,
                  message: "Please enter your new password!",
                },
                {
                  min: 8,
                  message: "Password must be at least 8 characters long!",
                },
                {
                  pattern: /[A-Z]/,
                  message:
                    "Password must contain at least one uppercase letter!",
                },
                {
                  pattern: /[a-z]/,
                  message:
                    "Password must contain at least one lowercase letter!",
                },
                {
                  pattern: /[0-9]/,
                  message: "Password must contain at least one number!",
                },
                {
                  pattern: /[!@#$%^&*(),.?":{}|<>]/,
                  message:
                    "Password must contain at least one special character!",
                },
              ]}
            >
              <Input.Password placeholder="New Password" />
            </Form.Item>

            <Form.Item
              name="confirmPassword"
              rules={[
                {
                  required: true,
                  message: "Please confirm your password!",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("password") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error("Passwords do not match!"));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm Password" />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
              >
                Change Password
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {showSuccess && !showFailure && (
        <SuccessResult
          title="Password Changed"
          subTitle="Your password has been successfully reset. Please go to the login page to access your account."
          onClick={handleGoToSignIn}
          buttonTitle="Go to login"
        />
      )}

      {showFailure && !showSuccess && (
        <FailureResult
          title="Reset Password Failed"
          subTitle=""
          onClick={handleTryAgain}
          reason={failureReasons}
        />
      )}
    </Flex>
  );
};

export default ResetPassword;
